<template>
  <div class="autocomplete" @click="focus">
    <div class="autocomplete__tags">
      <span
        class="autocomplete__tag"
        v-for="(selection, index) in selected"
        :key="index"
        >{{ selection.name }}</span
      >
    </div>
    <div class="autocomplete__input-container">
      <input
        type="text"
        v-model="query"
        class="autocomplete__input"
        @keydown.up="onKeyboardUp"
        @keydown.down="onKeyboardDown"
        @keydown.enter="onKeyboardEnter"
        @keydown.delete="onKeyboardBackspace"
        ref="input"
      />
      <ul
        class="autocomplete__suggestions member-autocomplete"
        ref="suggestions"
        :class="className"
        v-show="suggestions.length"
      >
        <li
          v-for="(suggestion, index) in suggestions"
          :key="index"
          @click="setSelected(suggestion)"
          class="autocomplete-item"
          :class="{
            'autocomplete-item--active': suggestionActiveIndex === index,
            'autocomplete-item--disabled':
              suggestion.in_group || suggestion.in_list
          }"
        >
          <div class="autocomplete-item__image">
            <img :src="suggestion.avatar_url" alt="" />
          </div>
          <div class="autocomplete-item__content">
            <div
              class="autocomplete-item__name"
              v-html="suggestion.highlighted_name"
            ></div>
            <div
              class="autocomplete-item__slug"
              v-if="!suggestion.in_group && !suggestion.in_list"
            >
              grabcad.com/<span
                v-html="suggestion.highlighted_cached_slug"
              ></span>
            </div>
            <div
              class="autocomplete-item__slug"
              v-if="suggestion.in_group || suggestion.in_list"
            >
              (already in this <span v-if="suggestion.in_group">group</span
              ><span v-if="suggestion.in_list && !suggestion.in_group"
                >list</span
              >)
            </div>
          </div>
        </li>
      </ul>
      <div class="autocomplete__suggestions" v-if="loadingCondition">
        <div class="circle-spinner">
          <div class="sk-circle1 sk-circle"></div>
          <div class="sk-circle2 sk-circle"></div>
          <div class="sk-circle3 sk-circle"></div>
          <div class="sk-circle4 sk-circle"></div>
          <div class="sk-circle5 sk-circle"></div>
          <div class="sk-circle6 sk-circle"></div>
          <div class="sk-circle7 sk-circle"></div>
          <div class="sk-circle8 sk-circle"></div>
          <div class="sk-circle9 sk-circle"></div>
          <div class="sk-circle10 sk-circle"></div>
          <div class="sk-circle11 sk-circle"></div>
          <div class="sk-circle12 sk-circle"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios'

  const MAX_ALLOWED_INVITATIONS = 10;
  const MIN_QUERY_LENGTH = 2;

export default {
  props: ["value"],
  data () {
    return {
      suggestions: [],
      suggestionActiveIndex: -1,
      query: "",
      selected: [],
      className: "",
      loading: false
    };
  },
  mounted () {
    document.addEventListener("click", this.handleClicksOutside);
    document.addEventListener("keyup", this.onKeyboardEscape);
  },
  destroyed () {
    document.removeEventListener("click", this.handleClicksOutside);
    document.removeEventListener("keyup", this.onKeyboardEscape);
  },
  computed: {
    allowedInvitationsLimitReached () {
      return this.selected.length >= MAX_ALLOWED_INVITATIONS;
    },
    queryLongEnough () {
      return this.query.trim().length >= MIN_QUERY_LENGTH;
    },
    loadingCondition () {
      return (
        this.loading &&
        this.query.length &&
        !this.suggestions.length &&
        !this.allowedInvitationsLimitReached
      );
    }
  },
  methods: {
    api () {
      return axios.create({
        baseURL: "/groups/" + Grabcad.currentGroup + "/member_search",
        params: { format: "json" }
      });
    },
    onChange () {
      this.clearSearch();
      this.setLoading();
      this.debounceSearch =
        this.debounceSearch || Grabcad.debounce(this.search, 250);
      this.debounceSearch();
    },
    search () {
      if (this.allowedInvitationsLimitReached) return;
      if (!this.queryLongEnough) return this.clearSearch();
      this.api()
        .get("", {
          params: {
            query: this.query
          }
        })
        .then(this.applySearchResults)
        .catch(this.clearLoading);
    },
    clearSearch () {
      this.clearLoading();
      this.suggestions = [];
      this.clearSelectedIndex();
    },
    clearQuery () {
      this.query = "";
      this.clearSearch();
    },
    setLoading () {
      if (this.allowedInvitationsLimitReached || this.query.length < 1) return;
      this.loading = true;
    },
    clearLoading () {
      this.loading = false;
    },
    applySearchResults (response) {
      this.suggestions = response.data.results.slice().map((item) => {
        item.in_list = this.exists(item);
        return item;
      });

      this.clearLoading();
      if (!this.suggestions.length) return;
      this.suggestionActiveIndex = 0;
    },
    setSelected (item) {
      if (this.exists(item)) return;
      if (item.in_group || item.in_list) return;
      if (this.allowedInvitationsLimitReached) return;
      this.selected.push(item);
      this.clearQuery();
    },
    cancelSelected (item) {
      this.selected.splice(this.selected.indexOf(item), 1);
    },
    clearSelectedIndex () {
      this.suggestionActiveIndex = -1;
    },
    onKeyboardUp (event) {
      event.preventDefault();
      if (this.suggestionActiveIndex > -1) this.suggestionActiveIndex--;
    },
    onKeyboardDown (event) {
      event.preventDefault();
      if (this.suggestions.length > this.suggestionActiveIndex + 1)
        this.suggestionActiveIndex++;
    },
    onKeyboardEnter (event) {
      event.preventDefault();
      this.clearLoading();
      if (
        this.suggestionActiveIndex < 0 ||
        this.suggestionActiveIndex > this.suggestions.length
      )
        return;
      this.setSelected(this.suggestions[this.suggestionActiveIndex]);
      this.focus();
    },
    onKeyboardEscape (event) {
      const key = event.which || event.keyCode;
      if (key === 27) {
        event.preventDefault();
        if (this.suggestions.length) {
          this.clearSearch();
        }
      }
    },
    onKeyboardBackspace (event) {
      if (!this.query.length) {
        event.preventDefault();
        this.selected.splice(this.selected.length - 1, 1);
      }
    },
    focus () {
      this.$refs.input.focus();
    },
    handleClicksOutside (event) {
      const el = this.$refs.suggestions;

      if (el && !el.contains(event.target)) {
        this.clearSearch();
      }
    },
    overflowCheck () {
      const el = this.$refs.suggestions;
      if (!el) return;
      const elementWidth = el.scrollWidth || el.offsetWidth || 320;
      const distanceFromRight =
        window.innerWidth - (el.getBoundingClientRect().right + elementWidth);
      const distanceFromLeft = el.getBoundingClientRect().left;
      if (distanceFromRight < 0 && distanceFromLeft > elementWidth) {
        this.className = "opens-left";
      } else {
        this.className = "";
      }
    },
    exists (newItem) {
      return (
        this.selected.filter(function(item) {
          return item.cached_slug === newItem.cached_slug;
        }).length > 0
      );
    }
  },
  watch: {
    query (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.onChange();
      }
    },
    suggestions () {
      setTimeout(Grabcad.debounce(this.overflowCheck, 200), 0);
    },
    selected (newValue) {
      this.$emit("input", newValue);
    },
    value (newValue) {
      this.selected = newValue;
    }
  }
};
</script>
